import React, { useState } from "react";
import {
  DataGrid,
  GridColDef,
  GridDeleteIcon,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import { Container, Tooltip, IconButton } from "@mui/material";
import { BreakageReport } from "../../../../domain/models/breakage-report";
import { CustomDialog } from "../../../components/Dialog";
import { DeleteBreakageReport } from "../../../../domain/usecases/delete-brekage-report";
import SaveIcon from "@mui/icons-material/Save";
import { UpdateBreakageReport } from "../../../../domain/usecases/update-breakage-report";

type Props = {
  data: BreakageReport[];
  deleteBreakage: DeleteBreakageReport;
  updateBreakage: UpdateBreakageReport;
  setBreakages: React.Dispatch<React.SetStateAction<BreakageReport[]>>;
};

export const BreakageTable = (props: Props) => {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const columns: GridColDef[] = [
    { field: "id", headerName: "ID", width: 0 },
    { field: "document", headerName: "Inserir NF", editable: true },
    {
      field: "product",
      headerName: "Produto",
      width: 400,
      valueGetter: (params) => {
        return params.row.inventory.item.description;
      },
    },
    {
      field: "batch",
      headerName: "Lote",
      width: 80,
      valueGetter: (params) => {
        return params.row.inventory.item.batch;
      },
    },
    {
      field: "position",
      headerName: "Posição",
      width: 80,
      valueGetter: (params) => {
        return `${params.row.inventory.position.street}.${
          params.row.inventory.position.building
        }${
          params.row.inventory.position.level ||
          params.row.inventory.position.level === 0
            ? "." +
              params.row.inventory.position.level +
              "." +
              params.row.inventory.position.apartment
            : ""
        }`;
      },
    },
    {
      field: "amount",
      headerName: "Quantidade",
      width: 100,
      valueGetter: (params) => {
        return `${
          params.row.amount / (params.row.inventory.item.packageAmount || 1)
        } (${params.row.amount.toFixed(2)} ${params.row?.inventory.item.unit})`;
      },
    },
    {
      field: "createdAt",
      headerName: "Data",
      width: 160,
      valueGetter: (params) => {
        const date = new Date(params.row.createdAt);
        return date.toLocaleString();
      },
    },
    {
      field: "user",
      headerName: "Usuário",
      width: 100,
      valueGetter: (params) => {
        return params.row.createdBy.username;
      },
    },
  ];
  return (
    <Container>
      <DataGrid
        rows={props.data}
        columns={columns}
        checkboxSelection
        slots={{ toolbar: CustomToolbar }}
        slotProps={{
          toolbar: {
            showDelete: selectedRows.length > 0,
            isLoading: isLoading,
            updateBreakage: async () => {
              if (!props.data.some((item) => !!item.document)) {
                alert("Não foram detectadas alterações");
              }
              await Promise.all(
                props.data.map((item) => {
                  if (item.document) {
                    return props.updateBreakage.update({
                      id: item.id,
                      document: item.document,
                      status: "SUBMITTED",
                    });
                  }
                })
              );
              props.setBreakages((old) => {
                return old.filter((report) => !report.document);
              });
              alert("Alterações salvas com sucesso!");
            },
            deleteBreakage: async () => {
              setIsLoading(true);
              try {
                await Promise.all(
                  selectedRows.map((id) => {
                    return props?.deleteBreakage?.delete(id);
                  })
                );
                props.setBreakages((old) => {
                  return old.filter(
                    (report) => !selectedRows.includes(report.id)
                  );
                });
                alert("Quebras deletadas com sucesso!");
              } catch (error: any) {
                alert("Ocorreu um erro inesperado, tente novamente.");
              } finally {
                setIsLoading(false);
              }
            },
          },
        }}
        onRowSelectionModelChange={(e) => {
          setSelectedRows(e as unknown as number[]);
        }}
        disableRowSelectionOnClick
        isRowSelectable={(row) => {
          return !row.row.document;
        }}
        onStateChange={(params) => {
          const obj = params.editRows;
          if (Object.keys(obj).length === 0) return;
          for (const key in params.editRows) {
            if (obj[key]?.document?.value === null) continue;
            props.setBreakages((old) => {
              const report = old.find(
                (breakage) => breakage.id === Number(key)
              );
              if (report) {
                report.document = obj[key]?.document?.value;
              }
              return old;
            });
          }
        }}
        autoHeight
        initialState={{
          columns: {
            columnVisibilityModel: {
              id: false,
            },
          },
        }}
      />
    </Container>
  );
};

function CustomToolbar(props: {
  showDelete: boolean;
  deleteBreakage: () => void;
  updateBreakage: () => void;
  isLoading: boolean;
}) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = React.useState(false);

  const handleCancelDialogDialogOpen = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleCancelDialogDialogClose = () => {
    setIsDeleteDialogOpen(false);
  };
  return (
    <GridToolbarContainer
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
      }}
    >
      <GridToolbarFilterButton />
      <CustomDialog
        title="Tem certeza que deseja deletar as quebras selecionadas?"
        content="Ao deletar, o estoque retornará à posição de origem e o registro da quebra será apagado."
        isOpen={isDeleteDialogOpen}
        yesText="Deletar"
        noText="Cancelar"
        onNo={handleCancelDialogDialogClose}
        handleClose={handleCancelDialogDialogClose}
        onYes={() => {
          props.deleteBreakage();
          handleCancelDialogDialogClose();
        }}
        isLoading={props.isLoading}
      />
      <CustomDialog
        title="Confirmar alterações?"
        isOpen={isUpdateDialogOpen}
        yesText="Confirmar"
        noText="Cancelar"
        onNo={() => {
          setIsUpdateDialogOpen(false);
        }}
        handleClose={() => {
          setIsUpdateDialogOpen(false);
        }}
        onYes={() => {
          props.updateBreakage();
          setIsUpdateDialogOpen(false);
        }}
        isLoading={props.isLoading}
      />
      <GridToolbarExport />
      {props.showDelete && (
        <Tooltip title="Delete">
          <IconButton onClick={handleCancelDialogDialogOpen}>
            <GridDeleteIcon color="primary" />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Salvar">
        <IconButton
          onClick={() => {
            setIsUpdateDialogOpen(true);
          }}
        >
          <SaveIcon color="primary" />
        </IconButton>
      </Tooltip>
    </GridToolbarContainer>
  );
}
